(() => {
  const ELEMENT_NAME = "ecomhard-kit-selector";
  const EVENT_NAME = "ecomhard-kit:gallery-updated";
  const fixedStates = new Map();

  function escapeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(String(value));

    return String(value).replace(
      /([ !"#$%&'()*+,./:;<=>?@[\]^`{|}~])/g,
      "\\$1"
    );
  }

  function getSectionId(element) {
    return String(element?.dataset?.sectionId || "");
  }

  function getGallery(sectionId, root = document) {
    if (!sectionId) return null;

    return (
      root.getElementById?.(`MediaGallery-${sectionId}`) ||
      root.querySelector?.(
        `media-gallery#MediaGallery-${escapeSelector(sectionId)}`
      ) ||
      null
    );
  }

  function getViewerList(gallery, sectionId) {
    if (!gallery) return null;

    return (
      gallery.querySelector(`#Slider-Gallery-${escapeSelector(sectionId)}`) ||
      gallery.querySelector(".product__media-list")
    );
  }

  function getThumbnailSlider(gallery, sectionId) {
    if (!gallery) return null;

    return (
      gallery.querySelector(
        `#GalleryThumbnails-${escapeSelector(sectionId)}`
      ) ||
      null
    );
  }

  function getThumbnailList(gallery, sectionId) {
    const thumbnailSlider = getThumbnailSlider(gallery, sectionId);

    return (
      thumbnailSlider?.querySelector(
        `#Slider-Thumbnails-${escapeSelector(sectionId)}`
      ) ||
      thumbnailSlider?.querySelector(".thumbnail-list") ||
      null
    );
  }

  function getProductModal(root, sectionId) {
    return (
      root.getElementById?.(`ProductModal-${sectionId}`) ||
      root.querySelector?.(
        `product-modal#ProductModal-${escapeSelector(sectionId)}`
      ) ||
      null
    );
  }

  function getModalContent(modal) {
    return modal?.querySelector(".product-media-modal__content") || null;
  }

  function getModalEntries(modal) {
    const content = getModalContent(modal);
    if (!content) return [];

    return Array.from(content.children).filter((child) => {
      return (
        child.matches?.("[data-media-id]") ||
        Boolean(child.querySelector?.("[data-media-id]"))
      );
    });
  }

  function getEntryMediaId(entry) {
    return String(
      entry?.dataset?.mediaId ||
        entry?.querySelector?.("[data-media-id]")?.dataset?.mediaId ||
        ""
    );
  }

  function normalizeRawMediaId(mediaId, sectionId) {
    const normalized = String(mediaId || "");
    const prefix = `${sectionId}-`;

    return normalized.startsWith(prefix)
      ? normalized.slice(prefix.length)
      : normalized;
  }

  function findModalEntry(modal, mediaId, sectionId) {
    const rawMediaId = normalizeRawMediaId(mediaId, sectionId);

    return (
      getModalEntries(modal).find((entry) => {
        const entryId = getEntryMediaId(entry);

        return (
          entryId === mediaId ||
          entryId === rawMediaId ||
          entryId.endsWith(`-${rawMediaId}`)
        );
      }) ||
      getModalEntries(modal)[0] ||
      null
    );
  }

  function createThumbnailFromSlide(slide, sectionId, index) {
    if (!slide) return null;

    const mediaId = String(slide.dataset.mediaId || "");
    const sourceImage = slide.querySelector("img");
    const item = document.createElement("li");
    const button = document.createElement("button");
    const image = sourceImage
      ? sourceImage.cloneNode(true)
      : document.createElement("img");
    const thumbnailId = `Thumbnail-${sectionId}-${index}`;

    item.id = `Slide-Thumbnails-${sectionId}-${index}`;
    item.className = "thumbnail-list__item slider__slide";
    item.dataset.target = mediaId;
    item.dataset.mediaPosition = String(index + 1);

    button.type = "button";
    button.className =
      "thumbnail global-media-settings global-media-settings--no-shadow";
    button.setAttribute(
      "aria-label",
      image.alt
        ? `Carregar imagem ${index + 1}: ${image.alt}`
        : `Carregar imagem ${index + 1}`
    );
    button.setAttribute(
      "aria-controls",
      `GalleryViewer-${sectionId}`
    );
    button.setAttribute("aria-describedby", thumbnailId);

    image.id = thumbnailId;
    image.loading = "lazy";
    image.classList.remove(
      "image-magnify-hover",
      "image-magnify-lightbox",
      "image-magnify-none"
    );

    button.append(image);
    item.append(button);

    return item;
  }

  function normalizeThumbnail(
    thumbnail,
    slide,
    sectionId,
    index,
    isActive
  ) {
    const normalized =
      thumbnail?.cloneNode(true) ||
      createThumbnailFromSlide(slide, sectionId, index);

    if (!normalized) return null;

    const mediaId = String(slide?.dataset?.mediaId || "");
    const thumbnailId = `Thumbnail-${sectionId}-${index}`;
    const button = normalized.querySelector("button");
    const image = normalized.querySelector("img");

    normalized.id = `Slide-Thumbnails-${sectionId}-${index}`;
    normalized.dataset.target = mediaId;
    normalized.dataset.mediaPosition = String(index + 1);
    normalized.classList.add(
      "thumbnail-list__item",
      "slider__slide"
    );

    if (button) {
      button.setAttribute(
        "aria-controls",
        `GalleryViewer-${sectionId}`
      );
      button.setAttribute("aria-describedby", thumbnailId);

      if (isActive) {
        button.setAttribute("aria-current", "true");
      } else {
        button.removeAttribute("aria-current");
      }
    }

    if (image) {
      image.id = thumbnailId;
    }

    return normalized;
  }

  function createBasicThumbnailSlider(
    gallery,
    sectionId,
    total
  ) {
    const mainProduct = document.getElementById(
      `MainProduct-${sectionId}`
    );
    const desktopLayout =
      gallery?.dataset?.desktopLayout ||
      mainProduct?.dataset?.galleryLayout ||
      "";
    const mobileThumbnails =
      mainProduct?.dataset?.mobileThumbnails || "hide";

    const slider = document.createElement("slider-component");
    const list = document.createElement("ul");

    slider.id = `GalleryThumbnails-${sectionId}`;
    slider.className =
      "thumbnail-slider slider-mobile-gutter quick-add-hidden";

    if (!desktopLayout.includes("thumbnail")) {
      slider.classList.add("medium-hide", "large-up-hide");
    }

    if (mobileThumbnails !== "show") {
      slider.classList.add("small-hide");
    }

    if (total <= 3) {
      slider.classList.add("thumbnail-slider--no-slide");
    }

    list.id = `Slider-Thumbnails-${sectionId}`;
    list.className =
      "thumbnail-list list-unstyled slider slider--mobile";

    if (desktopLayout === "thumbnail_slider") {
      list.classList.add("slider--tablet-up");
    }

    slider.append(list);
    gallery.append(slider);

    return slider;
  }

  function updateViewerControls(
    gallery,
    total,
    currentPosition = 1
  ) {
    const viewerButtons = gallery?.querySelector(
      '[id^="GalleryViewer-"] > .slider-buttons'
    );
    const current = viewerButtons?.querySelector(
      ".slider-counter--current"
    );
    const totalElement = viewerButtons?.querySelector(
      ".slider-counter--total"
    );

    viewerButtons?.classList.toggle("small-hide", total <= 1);

    if (current) {
      current.textContent = String(
        Math.min(Math.max(currentPosition, 1), total)
      );
    }

    if (totalElement) {
      totalElement.textContent = String(total);
    }
  }

  function updateThumbnailControls(thumbnailSlider, total) {
    if (!thumbnailSlider) return;

    thumbnailSlider.classList.toggle(
      "thumbnail-slider--no-slide",
      total <= 3
    );

    thumbnailSlider
      .querySelectorAll(".slider-button")
      .forEach((button) => {
        button.classList.toggle("small-hide", total <= 3);
        button.classList.toggle("medium-hide", total <= 4);
        button.classList.toggle("large-up-hide", total <= 4);
      });
  }

  function resetGalleryComponents(gallery) {
    if (!gallery) return;

    if (typeof gallery.refresh === "function") {
      gallery.refresh();
    }

    gallery.querySelectorAll("slider-component").forEach((slider) => {
      slider.resetPages?.();
      slider.initPages?.();
    });

    requestAnimationFrame(() => {
      gallery.querySelectorAll("slider-component").forEach((slider) => {
        slider.resetPages?.();
        slider.initPages?.();
      });
    });
  }

  function captureFixedState(selector) {
    const sectionId = getSectionId(selector);
    if (!sectionId || fixedStates.has(sectionId)) return;

    const gallery = getGallery(sectionId);
    const viewerList = getViewerList(gallery, sectionId);
    const fixedSlide = viewerList?.querySelector("[data-media-id]");

    if (!gallery || !viewerList || !fixedSlide) {
      console.warn(
        "[ECOMHARD Kits] O produto Kit X precisa ter ao menos uma mídia para fixar a primeira imagem."
      );
      return;
    }

    const fixedMediaId = String(
      fixedSlide.dataset.mediaId || ""
    );
    const thumbnailList = getThumbnailList(gallery, sectionId);
    const fixedThumbnail =
      thumbnailList?.querySelector(
        `[data-target="${fixedMediaId}"]`
      ) || thumbnailList?.querySelector("[data-target]");
    const modal = getProductModal(document, sectionId);
    const fixedModalEntry = findModalEntry(
      modal,
      fixedMediaId,
      sectionId
    );

    fixedStates.set(sectionId, {
      sectionId,
      pageProductId: String(
        selector.dataset.pageProductId ||
          gallery.dataset.productId ||
          ""
      ),
      slide: fixedSlide.cloneNode(true),
      thumbnail: fixedThumbnail?.cloneNode(true) || null,
      modalEntry: fixedModalEntry?.cloneNode(true) || null,
      modalSnapshot: modal?.cloneNode(true) || null,
    });
  }

  function captureAllFixedStates(root = document) {
    root
      .querySelectorAll?.(
        `${ELEMENT_NAME}[data-section-id]`
      )
      .forEach(captureFixedState);
  }

  function mergeModal(
    sectionId,
    fixedState,
    dynamicModal
  ) {
    let liveModal = getProductModal(document, sectionId);

    if (!liveModal && fixedState.modalSnapshot) {
      liveModal = fixedState.modalSnapshot.cloneNode(true);

      const mainProduct = document.getElementById(
        `MainProduct-${sectionId}`
      );
      const pageWidth = mainProduct?.querySelector(".page-width");

      pageWidth?.append(liveModal);
    }

    if (!liveModal) return;

    const liveContent = getModalContent(liveModal);
    if (!liveContent) return;

    const nonMediaChildren = Array.from(
      liveContent.children
    ).filter((child) => {
      return !(
        child.matches?.("[data-media-id]") ||
        child.querySelector?.("[data-media-id]")
      );
    });

    const dynamicEntries = getModalEntries(dynamicModal).map(
      (entry) => entry.cloneNode(true)
    );

    const finalEntries = [];

    if (fixedState.modalEntry) {
      finalEntries.push(fixedState.modalEntry.cloneNode(true));
    }

    dynamicEntries.forEach((entry) => {
      const entryId = getEntryMediaId(entry);
      const fixedId = getEntryMediaId(fixedState.modalEntry);

      if (!fixedId || entryId !== fixedId) {
        finalEntries.push(entry);
      }
    });

    liveContent.replaceChildren(
      ...nonMediaChildren,
      ...finalEntries
    );
  }

  function mergeGallery(
    sectionId,
    fixedState,
    dynamicGallery,
    dynamicModal,
    dynamicProductId
  ) {
    const liveGallery = getGallery(sectionId);
    const liveViewerList = getViewerList(
      liveGallery,
      sectionId
    );
    const dynamicViewerList = getViewerList(
      dynamicGallery,
      sectionId
    );

    if (!liveGallery || !liveViewerList || !dynamicViewerList) {
      return false;
    }

    const fixedSlide = fixedState.slide.cloneNode(true);
    const dynamicSlides = Array.from(
      dynamicViewerList.querySelectorAll(":scope > [data-media-id]")
    ).map((slide) => slide.cloneNode(true));

    const activeIndex = dynamicSlides.length > 0 ? 1 : 0;

    fixedSlide.classList.toggle(
      "is-active",
      activeIndex === 0
    );

    dynamicSlides.forEach((slide, index) => {
      slide.classList.toggle(
        "is-active",
        activeIndex === index + 1
      );
    });

    const finalSlides = [fixedSlide, ...dynamicSlides];
    const hasMultipleMedia = finalSlides.length > 1;

    finalSlides.forEach((slide) => {
      slide.classList.toggle(
        "product__media-item--single",
        !hasMultipleMedia
      );
    });

    liveViewerList.replaceChildren(...finalSlides);

    let thumbnailSlider = getThumbnailSlider(
      liveGallery,
      sectionId
    );
    const dynamicThumbnailList = getThumbnailList(
      dynamicGallery,
      sectionId
    );

    if (!thumbnailSlider && finalSlides.length > 1) {
      const dynamicThumbnailSlider = getThumbnailSlider(
        dynamicGallery,
        sectionId
      );

      if (dynamicThumbnailSlider) {
        thumbnailSlider =
          dynamicThumbnailSlider.cloneNode(true);
        liveGallery.append(thumbnailSlider);
      } else {
        thumbnailSlider = createBasicThumbnailSlider(
          liveGallery,
          sectionId,
          finalSlides.length
        );
      }
    }

    if (thumbnailSlider) {
      let liveThumbnailList =
        thumbnailSlider.querySelector(
          `#Slider-Thumbnails-${escapeSelector(sectionId)}`
        ) ||
        thumbnailSlider.querySelector(".thumbnail-list");

      if (!liveThumbnailList) {
        liveThumbnailList = document.createElement("ul");
        liveThumbnailList.id =
          `Slider-Thumbnails-${sectionId}`;
        liveThumbnailList.className =
          "thumbnail-list list-unstyled slider slider--mobile";
        thumbnailSlider.append(liveThumbnailList);
      }

      const dynamicThumbnails = dynamicSlides.map(
        (slide) => {
          const mediaId = String(
            slide.dataset.mediaId || ""
          );

          return (
            dynamicThumbnailList?.querySelector(
              `[data-target="${mediaId}"]`
            ) || null
          );
        }
      );

      const normalizedThumbnails = finalSlides
        .map((slide, index) => {
          const sourceThumbnail =
            index === 0
              ? fixedState.thumbnail
              : dynamicThumbnails[index - 1];

          return normalizeThumbnail(
            sourceThumbnail,
            slide,
            sectionId,
            index,
            index === activeIndex
          );
        })
        .filter(Boolean);

      liveThumbnailList.replaceChildren(
        ...normalizedThumbnails
      );

      updateThumbnailControls(
        thumbnailSlider,
        finalSlides.length
      );
    }

    updateViewerControls(
      liveGallery,
      finalSlides.length,
      activeIndex + 1
    );

    liveGallery.dataset.productId =
      fixedState.pageProductId;
    liveGallery.dataset.dynamicProductId = String(
      dynamicProductId || ""
    );

    mergeModal(
      sectionId,
      fixedState,
      dynamicModal
    );

    resetGalleryComponents(liveGallery);

    const activeSlide = finalSlides[activeIndex];
    const activeMediaId = String(
      activeSlide?.dataset?.mediaId || ""
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          activeMediaId &&
          typeof liveGallery.setActiveMedia === "function"
        ) {
          liveGallery.setActiveMedia(
            activeMediaId,
            false
          );
          return;
        }

        const viewerList = getViewerList(
          liveGallery,
          sectionId
        );

        if (viewerList && activeSlide) {
          viewerList.scrollTo?.({
            left: activeSlide.offsetLeft,
            behavior: "smooth",
          });
        }
      });
    });

    window.pauseAllMedia?.();

    return true;
  }

  function mergeCurrentGalleryAfterLegacyUpdate(event) {
    if (event.detail?.fixedFirstMerged) return;

    const sectionId = String(
      event.detail?.sectionId || ""
    );
    const fixedState = fixedStates.get(sectionId);
    const currentGallery = getGallery(sectionId);
    const currentModal = getProductModal(
      document,
      sectionId
    );

    if (!sectionId || !fixedState || !currentGallery) {
      return;
    }

    mergeGallery(
      sectionId,
      fixedState,
      currentGallery.cloneNode(true),
      currentModal?.cloneNode(true) || null,
      event.detail?.productId
    );
  }

  async function updateMainProductGalleryFixed(
    candidate,
    variant = null
  ) {
    const sectionId = getSectionId(this);
    const fixedState = fixedStates.get(sectionId);

    if (!fixedState) {
      captureFixedState(this);
    }

    const resolvedFixedState = fixedStates.get(sectionId);

    if (!resolvedFixedState) {
      return this.__ecomhardOriginalUpdateMainProductGallery?.call(
        this,
        candidate,
        variant
      );
    }

    const displayVariant =
      variant ||
      this.getDisplayVariantFromCandidate?.(candidate) ||
      null;
    const contextKey =
      `${candidate?.id || ""}:${displayVariant?.id || ""}`;
    const sectionUrl = this.getProductSectionUrl?.(
      candidate,
      displayVariant
    );

    if (!candidate || !sectionId || !sectionUrl) return;

    if (contextKey === this.currentGalleryContextKey) {
      return;
    }

    const currentGallery = getGallery(sectionId);
    if (!currentGallery) return;

    const requestId = ++this.galleryRequestId;
    this.galleryAbortController?.abort();
    this.galleryAbortController = new AbortController();

    currentGallery.setAttribute("aria-busy", "true");
    this.classList.add("is-gallery-loading");

    try {
      let sectionHtml = this.galleryCache?.get(sectionUrl);

      if (!sectionHtml) {
        const response = await fetch(sectionUrl, {
          method: "GET",
          headers: {
            Accept: "text/html",
            "X-Requested-With": "XMLHttpRequest",
          },
          cache: "no-store",
          signal: this.galleryAbortController.signal,
        });

        if (!response.ok) {
          throw new Error(
            `Não foi possível carregar a galeria (${response.status}).`
          );
        }

        sectionHtml = await response.text();
        this.galleryCache?.set(sectionUrl, sectionHtml);
      }

      if (requestId !== this.galleryRequestId) return;

      const parsedDocument =
        new DOMParser().parseFromString(
          sectionHtml,
          "text/html"
        );
      const dynamicGallery = getGallery(
        sectionId,
        parsedDocument
      );
      const dynamicModal = getProductModal(
        parsedDocument,
        sectionId
      );

      if (!dynamicGallery) {
        throw new Error(
          "A galeria do produto selecionado não foi encontrada."
        );
      }

      this.ensureGalleryColumnLayout?.(currentGallery);

      const merged = mergeGallery(
        sectionId,
        resolvedFixedState,
        dynamicGallery,
        dynamicModal,
        candidate.id
      );

      if (!merged) {
        throw new Error(
          "Não foi possível combinar as mídias do kit."
        );
      }

      this.currentGalleryProductId = String(candidate.id);
      this.currentGalleryContextKey = contextKey;

      document.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          detail: {
            source: "ecomhard-kits",
            productId: candidate.id,
            productHandle: candidate.handle,
            sectionId,
            fixedFirstMerged: true,
          },
        })
      );
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.error(
        "[ECOMHARD Kits] Não foi possível atualizar a galeria mantendo a primeira imagem fixa.",
        error
      );
    } finally {
      if (requestId === this.galleryRequestId) {
        getGallery(sectionId)?.removeAttribute(
          "aria-busy"
        );
        this.classList.remove("is-gallery-loading");
      }
    }
  }

  function showAwaitingSelection(slide) {
    if (!slide) return;

    slide.selectedProductId = null;

    slide.productChoiceInputs?.forEach((input) => {
      input.checked = false;
    });

    slide.candidates?.forEach((candidate) => {
      candidate.element.hidden = true;
      candidate.selections?.clear?.();

      candidate.inputs?.forEach((input) => {
        input.checked = false;
      });

      candidate.selectedValueElements?.forEach(
        (element) => {
          element.textContent = "";
        }
      );
    });

    if (slide.selectedProductLabelElement) {
      slide.selectedProductLabelElement.textContent = "";
    }

    if (slide.productTitleElement) {
      slide.productTitleElement.textContent =
        "Selecione uma opção";
    }

    if (slide.priceElement) {
      slide.priceElement.textContent = "";
    }

    if (slide.comparePriceElement) {
      slide.comparePriceElement.hidden = true;
      slide.comparePriceElement.textContent = "";
    }

    if (slide.imageElement) {
      slide.imageElement.style.opacity = ".35";
    }

    slide.element?.classList.add(
      "is-awaiting-selection"
    );
  }

  function patchElementClass(ElementClass) {
    if (
      !ElementClass ||
      ElementClass.prototype.__ecomhardFixedFirstPatched
    ) {
      return;
    }

    const prototype = ElementClass.prototype;

    prototype.__ecomhardOriginalCreateSlides =
      prototype.createSlides;

    prototype.createSlides = function (...args) {
      const result =
        this.__ecomhardOriginalCreateSlides?.apply(
          this,
          args
        );

      this.slides?.forEach((slide) => {
        showAwaitingSelection(slide);
      });

      this.currentGalleryContextKey = null;
      this.currentGalleryProductId = String(
        this.dataset.pageProductId || ""
      );

      return result;
    };

    prototype.__ecomhardOriginalUpdateSlideDisplay =
      prototype.updateSlideDisplay;

    prototype.updateSlideDisplay = function (slide) {
      if (!slide?.selectedProductId) {
        this.syncProductChoices?.(slide);
        showAwaitingSelection(slide);
        return;
      }

      slide.element?.classList.remove(
        "is-awaiting-selection"
      );

      if (slide.imageElement) {
        slide.imageElement.style.opacity = "1";
      }

      return this.__ecomhardOriginalUpdateSlideDisplay?.call(
        this,
        slide
      );
    };

    prototype.__ecomhardOriginalUpdateNavigation =
      prototype.updateNavigation;

    prototype.updateNavigation = function (...args) {
      const result =
        this.__ecomhardOriginalUpdateNavigation?.apply(
          this,
          args
        );

      const currentSlide =
        this.slides?.[this.currentIndex];
      const currentVariant =
        this.getSelectedVariant?.(currentSlide);
      const isLast =
        this.currentIndex ===
        (this.slides?.length || 1) - 1;
      const canAddAll =
        this.getAllSelectedVariants?.().every(Boolean);

      const emptySelectionLabel =
        this.currentIndex === 0
          ? "Selecione um item"
          : "Selecione um item";

      if (this.nextButton && !isLast) {
        this.nextButton.textContent = currentVariant
          ? "Próximo Item"
          : emptySelectionLabel;
      }

      if (this.addAllButton && isLast) {
        this.addAllButton.textContent = canAddAll
          ? "Adicionar todos"
          : emptySelectionLabel;
      }

      return result;
    };

    prototype.__ecomhardOriginalUpdateMainProductGallery =
      prototype.updateMainProductGallery;
    prototype.updateMainProductGallery =
      updateMainProductGalleryFixed;
    prototype.__ecomhardFixedFirstPatched = true;
  }

  function patchCustomElement() {
    patchElementClass(
      customElements.get(ELEMENT_NAME)
    );
  }

  /*
   * Intercepta o registro antes que o navegador faça o upgrade dos elementos.
   * Assim o createSlides já inicia sem produto pré-selecionado e nenhuma
   * atualização de galeria é disparada no carregamento inicial.
   */
  if (!window.__ecomhardKitDefineIntercepted) {
    const originalDefine =
      customElements.define.bind(customElements);

    customElements.define = function (
      name,
      constructor,
      options
    ) {
      if (name === ELEMENT_NAME) {
        patchElementClass(constructor);
      }

      return originalDefine(
        name,
        constructor,
        options
      );
    };

    window.__ecomhardKitDefineIntercepted = true;
  }

  captureAllFixedStates();

  document.addEventListener(
    EVENT_NAME,
    mergeCurrentGalleryAfterLegacyUpdate
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      captureAllFixedStates(event.target || document);
      patchCustomElement();
    }
  );

  customElements.whenDefined(ELEMENT_NAME).then(() => {
    patchCustomElement();

    document
      .querySelectorAll(
        `${ELEMENT_NAME}[data-section-id]`
      )
      .forEach((selector) => {
        selector.slides?.forEach((slide) => {
          if (!slide.selectedProductId) {
            showAwaitingSelection(slide);
          }
        });

        selector.updateNavigation?.();
      });
  });
})();
